
import useContentModule from '@/pages/composables/useContentModule'
import { ContentType } from '@/services/directr'
import { defineComponent, watch, onMounted, PropType, computed, toRefs, ref, reactive, ComputedRef } from 'vue'
import { useStore } from 'vuex'
import Barcode from '@/components/Barcode.vue'
import ScalingImage from '@/components/Image.vue'
import { CtCatalogue, CtCatalogueStockItem, CtStockItem } from '@/models/interfaces/content-type'
import { debounce, divideArray, uuid } from '@/utils'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'

// import useDiffSaveHandler, { SaveHooks } from '../composables/useDiffSaveHandler'
import useProductListState, { ProductListHandlers } from '../composables/useProductListState'

import TabNav, { TabView } from '@/components/TabNav.vue'
import Toggle from '@/components/Toggle.vue'
import StatusIndicator from '@/components/StatusIndicator.vue'
import ListHeader from '@/components/ListHeader.vue'
import SimpleLink from '@/components/SimpleLink.vue'
import CatalogueProductVariant from './CatalogueProductVariant.vue'
import _, { cloneDeep } from 'lodash'
import { date } from 'quasar'
import { DATE_DISPLAY_FORMAT_SHORT } from '@/constants'
import { format } from '@/utils/template'

// interface ProductDiffState {
//   lhs: Array<CtCatalogueProduct>,
//   rhs: Array<CtCatalogueProduct>
// }

// TODO: @Settings make global application setting
const DELIVERY_WARN_THRESHOLD = 10
const DEFAULT_PRICE_LIST_ITEM = () => ({
  id: uuid(),
  label: 'Single',
  value: 0,
  quantity: 1,
  active: true
})

export default defineComponent({
  name: 'CatalogueStockItemList',
  components: { TabNav, Toggle, StatusIndicator, ListHeader, SimpleLink, CatalogueProductVariant, Barcode, ScalingImage },
  props: {
    linkType: {
      type: String as PropType<ContentType>,
      default: ContentType.StockItem
      },
    catalogue: {
      type: Object as PropType<CtCatalogue>,
      required: true
    },
    label: {
      type: String,
      default: 'Stock Items'
    },
    columns: {
      type: [Number, Array],
      default: 24
    },
    entryConfig: {
      type: Object,
      default: null
    },
    itemConfig: {
      type: Object,
      default: null
    },
    selectFields: {
      type: Array,
      default: () => (['title', 'ean', 'price', 'images'])
    },
    tabs: {
      type: Array as PropType<Array<TabView>>,
      default: () => [
        {id: 'prices', label: 'Prices'},
        {id: 'details', label: 'Product Details'}
        // {id: 'order', label: 'Add to Order'}
      ]
    },
    defaultTab: {
      type: String,
      default: 'details'
    }
  },
  emits: ['diff', 'loaded', 'save-products'],
  setup (props, { emit }) {
    const $store = useStore()

    const queryFn = (item, chunk) => false

    // NOTE stores starting state and edited state, i.e left-hand-side <> right-hand-side
    // const diffState = reactive<ProductDiffState>({ lhs: [], rhs: [] })
    // const currentDiff = computed(() => diff(diffState.lhs, diffState.rhs))

    const contentModule = useContentModule<CtCatalogueStockItem>('CatalogueStockItem', $store)
    const { items, active, model, loading, fetch, mutate, requestHandler } = contentModule

    const { model: productModel } = useContentModule<CtStockItem>('StockItems', $store)
    void $store.dispatch('View/SetListActions', { remove: true, create: true, add: false, search: true, allow: { delete: true } })

    const draft:ProductListHandlers<CtStockItem, CtCatalogueStockItem>['draft'] = (type, product, catalogue) => ({
      sys: { id: uuid() , draft: true, type },
      id: product.sys.id,
      catalogue: catalogue.sys.id as UUID,
      // title: product.title,
      supply_chain_tracking_id: '',
      price_list: [DEFAULT_PRICE_LIST_ITEM()],
      price_lock: false,
      meta: {},
      active: true
    })

    const {
      state,
      applyQuery,
      processRemovalQueue,
      getCatalogueVariantIndex,
      updateProductStore,
      toggleRemoval,
      editItemLink,
      updateLink,
      openSearch,
      add,
      select,
      expandItem,
      addVariant,
      // @Diff -------
      save,
      saveState,
      diffState,
      currentDiff,
      registerHook,
      showSaveResult
    } = useProductListState<CtStockItem, CtCatalogueStockItem>(props, contentModule, $store, {draft}, queryFn)

    watch(() => diffState.rhs, () => emit('diff', !!currentDiff.value))

    const createTabState = (ds = diffState.rhs) => !ds ? {} : Object.fromEntries(ds.map(s => ([s.sys.id, props.tabs.find(f => f.id === props.defaultTab)])))
    const tabState = ref(createTabState())

    watch(() => state.selection, value => {
      value?.forEach(id => {
        // id = product id
        if (!tabState[id]) tabState[id] = [id, props.tabs.find(f => f.id === props.defaultTab)]
      })
    })

    function checkDeliveryThreshold (item) {
      if (!item.deliverable_to) return false
      return date.getDateDiff(Date.now(), item.deliverable_to, 'days') < DELIVERY_WARN_THRESHOLD
    }

    function addItemPrice (item:CtCatalogueStockItem) {
      item.sys.editing = true
      if (!item.price_list) item.price_list = [DEFAULT_PRICE_LIST_ITEM()]
      else item.price_list.push(DEFAULT_PRICE_LIST_ITEM())
    }

    async function updateItemPriceList (item:CtCatalogueStockItem) {
      item.sys.loading = true
      const updated = await requestHandler.patch(item.sys.id, { price_list: item.price_list })
      item.sys.loading = false
      item.sys.editing = false
    }

    function emitSave () {
      emit('save-products')
    }

    function init () {
      if (props.catalogue.sys.id === undefined || props.catalogue.sys.id === null) return

      // fetch all catalogue products matching the given catalogue sys.id
      fetch([{ field: 'catalogue', method: 'is', match: props.catalogue.sys.id }])
        .then(startingState => {
          diffState.lhs = cloneDeep(startingState)
          diffState.rhs = startingState || []
          // console.log('set rhs', diffState.rhs)

          tabState.value = createTabState()

          emit('loaded', startingState)

          // resolve linked product for every catalogue stock item
          const uuidChunks = divideArray((startingState || []).map(catalogue_product => catalogue_product.id).filter(Boolean), 100)
          Promise.all(uuidChunks.map(chunk => requestHandler.get(props.linkType, [{ select: props.selectFields }, chunk], { resolve: 2 }).then(({items = []}) => items)))
            .then((chunks) => {
              const items = chunks.flat(1)
              state.products = state.products.concat(items)
              // console.log('set products', state.products)

              // creates an easily accesible array of product id -> variant ids
              // NOTE the keys here are the PRODUCT sys.id not the CATALOGUE_PRODUCT sys.id
              // items.forEach((p:CtStockItem) => {
              //   if (p.variants) state.variants[p.sys.id] = p.variants.filter(f => !!f && f !== 'NULL')
              // })
              // console.log('set variants', state.variants)

            }).catch(console.error)
        })
        .catch(console.error)
    }

    const setQuery = debounce((query) => {
      state.productQuery = query
    }, 300)

    onMounted(init)
    watch(() => props.catalogue, init)

    return {
      ...toRefs(state),
      ...toRefs(saveState),
      tabState,
      productModel,
      filteredProducts: computed(() => { // filters out product that are already linked to a catalogue_product
        return state.products.map(p => {
          if (!p.meta?.synced_at && p.meta?.stock?.synced_at)
            p.meta.synced_at = p.meta.stock?.synced_at

          return p
        })
          .filter(p => diffState.rhs.findIndex(i => i.id === p.sys.id) < 0)
          .sort((x,y) => {
            const xs = x.meta?.synced_at || x.meta?.stock?.synced_at
            const ys = y.meta?.synced_at || y.meta?.stock?.synced_at

            return (!xs || !ys) ? 0 : ((new Date(xs).valueOf() > new Date(ys).valueOf()) ? 1 : -1)
          })
      }),
      currentDiff,
      diffState,
      filteredCatalogueProducts: computed(() => {
        // return diffState.rhs
        // only works if the product id for the catalogue_product is already resolved
        return diffState.rhs
          .filter(applyQuery)
          .filter(catalogue_product => !Object.keys(state.variants).length || !Object.values(state.variants).find(f => (f as Array<string>).includes(catalogue_product.id)))
      }),
      setQuery,
      getCatalogueVariantIndex,
      updateProductStore,
      toggleRemoval,
      openSearch,
      editItemLink,
      add,
      select,
      expandItem,
      addVariant,
      save,
      emitSave,
      loading,
      active,
      model,
      mutate,
      requestHandler,
      format,
      dateFormat: DATE_DISPLAY_FORMAT_SHORT,
      checkDeliveryThreshold,
      addItemPrice,
      updateItemPriceList,
      reloadPage: () => window.location.reload(),
      handleSelectionCommit: () => {
        !state.editingItemLink
          ? add()
          : updateLink()
      },
      openProductEditor: e => {
        // console.log('openProductEditor', e)
      }
      // checkInput: event => {
      //   console.log(event.key)
      //   if (event.key === ',') console.log('No Comma Bitch!')
      // }
    }
  }
})
